// import { ApiService } from "./../api/api.service";
// import { message } from "antd";
// import { useEffect, useState } from "react";
// // import { auditService } from "./audit.service";

// export { type AuditResult, type AuditRule, type FixResult } from "api/negotiation.service";

// function htmlToPlainText(html: string): string {
//   return html
//     .replace(/<[^>]*>/g, " ")
//     .replace(/\s+/g, " ")
//     .trim();
// }

// export function useEmailAudit() {
//   const [isOpen, setIsOpen] = useState(false);
//   const [selected, setSelected] = useState<Set<string>>(new Set());

//   const auditMutation = ApiService.negotiation.useAuditOutbound();
//   const fixMutation = ApiService.negotiation.useApplyFix();

//   // Pre-select all violations when fresh audit results arrive
//   useEffect(() => {
//     if (auditMutation.data) {
//       setSelected(new Set(auditMutation.data.result.invalid_rules.map((r) => r.key)));
//     }
//   }, [auditMutation.data]);

//   const analyze = (html: string, subject: string, orgCode?: string, messageId?: string) => {
//     setIsOpen(true);
//     fixMutation.reset();
//     setSelected(new Set());
//     if (!orgCode || !messageId) {
//       return;
//     }

//     auditMutation.mutate(
//       {
//         body: {
//           org_code: orgCode,
//           message_id: messageId,
//           email: { subject, body: htmlToPlainText(html) },
//         },
//       },
//       {
//         onError: () => {
//           message.error("Failed to audit email. Please try again.");
//           setIsOpen(false);
//         },
//       },
//     );
//   };

//   /** Send selected violation keys to the fix API. */
//   const fix = (messageId?: string) => {
//     if (selected.size === 0 || !messageId) return;

//     fixMutation.mutate(
//       {
//         body: {
//           selected_fixes: Array.from(selected).map((str) => ({ key: str })),
//           message_id: messageId,
//         },
//       },
//       {
//         onError: () => {
//           message.error("Failed to generate fix. Please try again.");
//         },
//       },
//     );
//   };

//   const discardFix = () => {
//     fixMutation.reset();
//   };

//   /** Toggle a single violation checkbox. */
//   const toggleSelected = (key: string) => {
//     setSelected((prev) => {
//       const next = new Set(prev);
//       next.has(key) ? next.delete(key) : next.add(key);
//       return next;
//     });
//   };

//   const close = () => {
//     setIsOpen(false);
//     auditMutation.reset();
//     fixMutation.reset();
//     setSelected(new Set());
//   };

//   return {
//     // state
//     isOpen,
//     isAnalyzing: auditMutation.isPending,
//     auditResult: auditMutation.data ?? null,
//     isFixing: fixMutation.isPending,
//     fixResult: fixMutation.data ?? null,
//     selected,
//     // actions
//     analyze,
//     fix,
//     discardFix,
//     toggleSelected,
//     close,
//   };
// }

import { ApiService } from "./../api/api.service";
import { message } from "antd";
import { useEffect, useState } from "react";

export { type AuditResult, type AuditRule, type FixResult } from "api/negotiation.service";

function htmlToPlainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// ── messageId added as hook param so GET queries can run on mount ─────────────
export function useEmailAudit(messageId?: string) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Tracks when user explicitly discards a saved fix so it doesn't re-appear
  const [fixDiscarded, setFixDiscarded] = useState(false);

  const auditMutation = ApiService.negotiation.useAuditOutbound();
  const fixMutation = ApiService.negotiation.useApplyFix();

  // ── GET saved results on mount (cache-first) ──────────────────────────────
  const savedAuditQuery = ApiService.negotiation.useGetAuditResults(messageId);
  const savedFixQuery = ApiService.negotiation.useGetAppliedFix(messageId);

  // ── Derived results — mutation data is fresher, saved data is the fallback ─
  const auditResult = auditMutation.data ?? savedAuditQuery.data ?? null;

  const fixResult = fixDiscarded
    ? null // user discarded → ignore saved fix
    : (fixMutation.data ?? savedFixQuery.data ?? null); // mutation > saved > nothing

  // ── Watch derived auditResult (not just mutation) so saved data also inits selected
  useEffect(() => {
    if (auditResult) {
      setSelected(new Set(auditResult.result.invalid_rules.map((r) => r.key)));
    }
  }, [auditResult]);

  // ── isAnalyzing: true when mutating OR when panel is open and saved query
  //    is still on its first fetch (isLoading = isPending + actively fetching)
  const isAnalyzing = auditMutation.isPending || (isOpen && savedAuditQuery.isLoading && !auditResult);

  // ── Actions ───────────────────────────────────────────────────────────────

  const analyze = (html: string, subject: string, orgCode?: string, msgId?: string) => {
    setIsOpen(true);
    fixMutation.reset();
    setFixDiscarded(false);
    setSelected(new Set());

    if (!orgCode || !msgId) return;

    // ① Saved audit already loaded → use it, no POST needed
    if (savedAuditQuery.data) return;

    // ② Saved query still fetching → skeleton shows via isAnalyzing, wait for it
    if (savedAuditQuery.isLoading) return;

    // ③ No saved data → run a fresh audit
    auditMutation.mutate(
      {
        body: {
          org_code: orgCode,
          message_id: msgId,
          email: { subject, body: htmlToPlainText(html) },
        },
      },
      {
        onError: () => {
          message.error("Failed to audit email. Please try again.");
          setIsOpen(false);
        },
      },
    );
  };

  const fix = (msgId?: string) => {
    if (selected.size === 0 || !msgId) return;

    // ① Saved fix already exists and user hasn't discarded it → use it, no POST needed
    if (savedFixQuery.data && !fixDiscarded) return;

    // ② No saved fix (or discarded) → generate a new one
    fixMutation.mutate(
      {
        body: {
          selected_fixes: Array.from(selected).map((str) => ({ key: str })),
          message_id: msgId,
        },
      },
      {
        onError: () => {
          message.error("Failed to generate fix. Please try again.");
        },
      },
    );
  };

  const discardFix = () => {
    fixMutation.reset();
    setFixDiscarded(true); // suppress savedFixQuery.data from re-appearing
  };

  const toggleSelected = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const close = () => {
    setIsOpen(false);
    auditMutation.reset();
    fixMutation.reset();
    setFixDiscarded(false);
    setSelected(new Set());
  };

  return {
    // state
    isOpen,
    isAnalyzing,
    auditResult,
    isFixing: fixMutation.isPending,
    fixResult,
    selected,
    // actions
    analyze,
    fix,
    discardFix,
    toggleSelected,
    close,
  };
}
